import { useCallback, useEffect, useRef, useState } from 'react';

import type { AuthContextState, AuthenticationState } from '../AuthContext';
import { authenticationRequestError, type TrackError } from '../helpers';
import type { User } from '../User';

export type SessionLoader = () => Promise<User | null>;

export function useSession(
  loadSession: SessionLoader,
  trackError?: TrackError,
): Pick<AuthContextState, 'authentication' | 'refresh'> {
  const requestId = useRef(0);
  const trackErrorRef = useRef(trackError);
  useEffect(() => {
    trackErrorRef.current = trackError;
  }, [trackError]);
  const [session, setSession] = useState<{
    loader: SessionLoader;
    authentication: AuthenticationState;
  }>({
    loader: loadSession,
    authentication: { state: 'authenticating', user: undefined },
  });
  const readSession = useCallback(() => {
    const id = ++requestId.current;
    async function resolveSession() {
      let authentication: AuthenticationState;
      try {
        const user = await loadSession();
        authentication = user
          ? { state: 'authenticated', user }
          : { state: 'not_authenticated', user: null };
      } catch (error) {
        if (id !== requestId.current) return;
        authentication = authenticationRequestError(error, trackErrorRef.current);
      }
      if (id !== requestId.current) return;
      setSession({ loader: loadSession, authentication });
    }
    void resolveSession();
  }, [loadSession]);

  const refresh = useCallback(() => {
    setSession({
      loader: loadSession,
      authentication: { state: 'authenticating', user: undefined },
    });
    readSession();
  }, [loadSession, readSession]);

  useEffect(() => {
    readSession();
    return () => {
      requestId.current += 1;
    };
  }, [readSession]);

  const refreshRef = useRef(refresh);
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    // A cached page can retain an identity that signed out in another tab.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        refreshRef.current();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  const authentication: AuthenticationState =
    session.loader === loadSession
      ? session.authentication
      : { state: 'authenticating', user: undefined };
  return { authentication, refresh };
}
